/**
 * @isg/ui-kit — barrel export for all JS modules.
 *
 * Apps can import individual modules for tree-shaking, or use this
 * barrel for convenience:
 *
 *   import { store, router, api, auth, showToast } from '@isg/ui-kit';
 */

// State
export { store, pageScope } from './state/store.js';

// Router
export { Router, router } from './utils/router.js';

// Services
export { ApiClient, ApiError, api, localeHeaders } from './services/api.js';
export { auth } from './services/auth.js';
export { renderLoginPage } from './components/login.js';
export { createChart, getChart, destroyAllCharts, CHART_COLORS, PALETTE,
         PRESETS, withPreset } from './components/charts.js';
export { applyTheme, getTheme, getResolvedTheme, initTheme } from './services/theme.js';
export { initAppUpdate } from './services/app-update.js';
export { loadBranding, getBrandingUrl, applyBranding, clearBrandingCache } from './services/branding.js';
export { checkTestMode, isTestMode, setTestMode, renderTestBanner, renderBetaBanner, renderDevBanner, checkEnvBanner } from './services/test-mode.js';

// Components
export { renderNavigation, applyUserAvatar, renderMobileHeader, createSidebarOverlay, closeMobileSidebar, getIcon } from './components/navigation.js';
export { showToast, showSuccess, showError, showInfo, showWarning, dismissToast, toast } from './components/toast.js';
export { openViewer, closeViewer, mountPreview } from './components/doc-viewer.js';
export { showFolderManager } from './components/folder-manager.js';
/* 🔴 file-manager.js is deliberately NOT re-exported here. It is the one
   ui-kit module with a runtime dependency (sortablejs), and this barrel is
   imported by every app's main.js — so a static re-export puts ~13 kB gzip
   of drag library into the ENTRY chunk of all 16 apps, for a feature two
   screens use. Measured: it landed in the intranet's index chunk, not its
   documents chunk.
   ⚠️ Tree-shaking cannot save us. ui-kit has no `sideEffects: false` on
   purpose — `initGlobalEscDismiss()` and the dirty guard are top-level calls
   that must never be shaken out — so the CSS import in file-manager.js keeps
   the whole module alive.
   Consumers deep-import it instead, which the package's `./js/*` export
   already allows and which puts it in the chunk that actually uses it:

     import { mountFolderTree } from '@isg/ui-kit/js/components/file-manager.js';
*/
export { mountDocAdmin } from './components/doc-admin.js';
// Hot Topics management — one page any team uses to publish into the
// intranet's single Hot Topics widget. Backend: isg_common.hot_topics.
export { mountHotTopicAdmin } from './components/hot-topic-admin.js';
export {
  getFileIcon, docThumbTile, wireDocThumbs,
  docViewToggle, wireDocViewToggle, renderDocList, wireDocTiles,
  readDocViewMode, writeDocViewMode,
} from './components/doc-tiles.js';
export { showModal, confirm } from './components/modal.js';
// One popover menu for the estate — button-anchored (attachExportMenu) and
// right-click (attachContextMenu). See components/menu.js for why it is here.
export { attachContextMenu, attachExportMenu, closeMenu, exportButtonHtml } from './components/menu.js';
export { renderAgentWidget } from './components/agent-widget.js';
// "Where does this number come from?" — the ⓘ beside a figure (isg_common.figures).
export { explainButton, wireExplain, openExplain } from './components/explain.js';
export { createNotificationPanel } from './components/notification-panel.js';
// Tables — sortable headers + a text filter for lists held in hand (promoted from
// isg-sales on 2026-10-26; the app passes every visible string, so nothing here needs t()).
export {
  sortState, sortTh, sortArrow, wireSort, sortRows,
  filterRows, filterInput, wireFilter, filterCount, foldText,
  filterState, facetOptions, filterBar, wireFilterBar, applyFilters, setFilterCount,
} from './components/table-tools.js';
// A division's month at a glance — four numbers and three pictures (BI Markets + Sales Market insights, 2026-10-26)
export { renderGlanceView } from './components/glance-view.js';
export {
  pushSupported, getPushState, enablePush, disablePush, sendTestPush,
  syncPushSubscription,
} from './services/push.js';
export { mountPushToggle } from './components/push-toggle.js';
export { maybeForcePasswordChange } from './components/force-password-change.js';

// Internationalisation — the estate's four locales (en-GB base, en-US, de, cs).
// An app calls initI18n() once at boot with its own catalogue + loader; every
// ui-kit component then renders in the chosen language with no further wiring.
export {
  initI18n, t, getLocale, setLocale, onLocaleChange, getAvailableLocales,
  registerAppMessages, registerChromeMessages, normaliseLocale,
  hasAppMessages, startAppI18n,
  DEFAULT_LOCALE, LOCALES, SUPPORTED,
} from './i18n/index.js';
export { CURRENCIES } from './i18n/locales.js';

// Utils — formatters
export {
  formatDate, formatDateTime, formatCurrency, timeAgo, daysUntil,
  capitalize, truncate,
  formatNumber, formatFileSize, readTime,
  sensitivityInfo, sourceLabel,
} from './utils/formatters.js';

// Utils — helpers
export { debounce, escapeHtml, decodeHtmlEntities, uid, el, sleep } from './utils/helpers.js';

// Utils — datetime-local conversion. 🔴 A datetime-local value is local
// wall-clock with NO offset; posting it raw made every scheduled thing in the
// estate an hour late for the whole of BST. Read the module header.
export { localInputToISO, isoToLocalInput, viewerTimeZone } from './utils/datetime.js';

// Global Escape-to-close for hand-rolled modals (ecosystem-wide). Installed
// once on barrel import; showModal()'s own Esc handling is unaffected.
import { initGlobalEscDismiss } from './utils/esc-dismiss.js';
export { initGlobalEscDismiss } from './utils/esc-dismiss.js';
initGlobalEscDismiss();

// BETA-00220 — an accidental Escape or backdrop click must not bin what
// somebody typed. Installed HERE, beside the Esc handler, because both are
// global behaviours every app inherits simply by importing ui-kit; there is
// no `sideEffects:false`, so this top-level call is never tree-shaken.
// ⚠️ Order matters: the guard registers in the CAPTURE phase, so it runs
// before esc-dismiss (bubble) and before showModal's own handler.
import { initDirtyGuard } from './utils/dirty-guard.js';
export { initDirtyGuard, clearDirty, isDirty } from './utils/dirty-guard.js';
initDirtyGuard();

// Auto-recover from stale lazy-import chunks after a redeploy (ecosystem-wide).
// A long-lived tab still references the previous build's hashed chunk names, so
// the next lazy navigation 404s ("Failed to fetch dynamically imported
// module"). This reloads once to pull the fresh index.html instead of showing
// the error. Installed once on barrel import.
import { initStaleChunkReload } from './utils/stale-chunk-reload.js';
export { initStaleChunkReload } from './utils/stale-chunk-reload.js';
initStaleChunkReload();

// 🔴 Images that lapsed with the session. `/uploads/**` needs a session and
// answers 404 when it is gone; an <img> carries only the cookie and NEVER
// retries, so a lapsed cookie blanked every avatar, banner and thumbnail on
// the page while the page itself kept working. This installs the proactive
// token refresh (so the cookie rarely lapses) and a bounded one-shot retry
// for the images that fail anyway. Installed once on barrel import, like the
// Esc-dismiss and dirty guards.
import { initImageRecovery } from './services/image-recovery.js';
export { initImageRecovery } from './services/image-recovery.js';
initImageRecovery();
